/**
 * WaterPhysics - Sistema de flujo de agua estilo Minecraft
 *
 * El agua se comporta asi:
 * - Cae hacia abajo si hay aire debajo (prioridad maxima)
 * - Se esparce horizontalmente hasta 4 bloques desde la fuente
 * - Cuando se rompe un bloque adyacente al agua, el agua fluye al hueco
 * - Maximo 50 actualizaciones por tick para mantener rendimiento
 *
 * Usa un sistema de "nivel de agua": fuente = 7, cada expansion reduce en 1.
 * Nivel 0 = agua desaparece. Solo los bloques fuente (nivel 7) son permanentes.
 */

import { BlockId } from './BlockType.js';

// Agua tiene niveles de 1-7, fuente = 7
// Almacenamos el nivel en un Map separado ya que los chunks solo guardan block IDs
const WATER_SOURCE_LEVEL = 7;
const MIN_WATER_LEVEL = 1;

export class WaterPhysics {
  constructor(world) {
    this.world = world;
    // Cola de bloques de agua pendientes de procesar {x, y, z}
    this.pendingUpdates = [];
    // Set para evitar duplicados en la cola: "x,y,z"
    this.pendingSet = new Set();
    // Mapa de niveles de agua: "x,y,z" -> level (1-7)
    // Los bloques de agua que no estan en este mapa se consideran fuente (7)
    this.waterLevels = new Map();
    // Timer de actualizacion
    this.updateTimer = 0;
    // Intervalo entre ticks de agua (segundos)
    this.UPDATE_INTERVAL = 0.3;
    // Maximo de updates por tick
    this.MAX_UPDATES_PER_TICK = 50;
  }

  /**
   * Obtener la clave de posicion para Maps/Sets
   */
  _key(x, y, z) {
    return `${x},${y},${z}`;
  }

  /**
   * Obtener el nivel de agua de un bloque.
   * Retorna 0 si no es agua, 7 si es fuente, 1-6 si es flujo.
   */
  getWaterLevel(x, y, z) {
    const block = this.world.getBlock(x, y, z);
    if (block !== BlockId.WATER) return 0;
    const key = this._key(x, y, z);
    if (this.waterLevels.has(key)) {
      return this.waterLevels.get(key);
    }
    // Si es agua pero no tiene nivel registrado, es fuente (generada por terreno o puesta por jugador)
    return WATER_SOURCE_LEVEL;
  }

  /**
   * Establecer el nivel de agua de un bloque.
   * Si level = WATER_SOURCE_LEVEL, eliminamos la entrada (es fuente por defecto).
   * Si level <= 0, el agua desaparece.
   */
  _setWaterLevel(x, y, z, level) {
    const key = this._key(x, y, z);
    if (level <= 0) {
      this.waterLevels.delete(key);
      if (this.world.getBlock(x, y, z) === BlockId.WATER) {
        this.world.setBlock(x, y, z, BlockId.AIR);
      }
    } else if (level >= WATER_SOURCE_LEVEL) {
      this.waterLevels.delete(key); // fuente no necesita entrada
    } else {
      this.waterLevels.set(key, level);
    }
  }

  /**
   * Programar una actualizacion para un bloque de agua.
   * No agrega duplicados.
   */
  scheduleUpdate(x, y, z) {
    const key = this._key(x, y, z);
    if (!this.pendingSet.has(key)) {
      this.pendingSet.add(key);
      this.pendingUpdates.push({ x, y, z });
    }
  }

  /**
   * Actualizacion principal - llamar cada frame con deltaTime.
   */
  update(dt) {
    this.updateTimer += dt;
    if (this.updateTimer < this.UPDATE_INTERVAL) return;
    this.updateTimer = 0;

    // Procesar hasta MAX_UPDATES_PER_TICK actualizaciones
    const count = Math.min(this.MAX_UPDATES_PER_TICK, this.pendingUpdates.length);
    const batch = this.pendingUpdates.splice(0, count);
    for (const { x, y, z } of batch) {
      this.pendingSet.delete(this._key(x, y, z));
      this._processWaterBlock(x, y, z);
    }
  }

  /**
   * Procesar un bloque de agua individual.
   * Logica de flujo:
   * 1. Si hay aire abajo, el agua cae (prioridad)
   * 2. Si no puede caer, se esparce horizontalmente
   * 3. Si perdio su fuente, se seca
   */
  _processWaterBlock(x, y, z) {
    const block = this.world.getBlock(x, y, z);
    if (block !== BlockId.WATER) return;

    const myLevel = this.getWaterLevel(x, y, z);
    if (myLevel <= 0) return;

    // --- 1. Caida: el agua cae si hay aire abajo ---
    if (y > 0) {
      const below = this.world.getBlock(x, y - 1, z);
      if (below === BlockId.AIR) {
        // Colocar agua abajo con el mismo nivel (la caida no reduce nivel)
        this.world.setBlock(x, y - 1, z, BlockId.WATER);
        // El agua que cae mantiene nivel alto para esparcirse al tocar fondo
        this._setWaterLevel(x, y - 1, z, myLevel);
        this.scheduleUpdate(x, y - 1, z);
        // No retornar - tambien verificar expansion horizontal
        // Pero priorizar la caida primero, programar re-chequeo
        return;
      }
    }

    // --- 2. Expansion horizontal ---
    // Solo expandirse si el nivel es > 1
    if (myLevel <= MIN_WATER_LEVEL) return;

    const newLevel = myLevel - 1;
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];

    for (const [dx, dz] of dirs) {
      const nx = x + dx;
      const nz = z + dz;
      const neighbor = this.world.getBlock(nx, y, nz);

      if (neighbor === BlockId.AIR) {
        // Expandir: colocar agua con nivel reducido
        this.world.setBlock(nx, y, nz, BlockId.WATER);
        this._setWaterLevel(nx, y, nz, newLevel);
        this.scheduleUpdate(nx, y, nz);
      } else if (neighbor === BlockId.WATER) {
        // Si el vecino ya es agua pero con nivel menor, actualizar
        const neighborLevel = this.getWaterLevel(nx, y, nz);
        if (neighborLevel < newLevel) {
          this._setWaterLevel(nx, y, nz, newLevel);
          this.scheduleUpdate(nx, y, nz);
        }
      }
    }
  }

  /**
   * Llamar cuando un bloque cambia en el mundo.
   * Detecta cuando un bloque se rompe cerca del agua o cuando se coloca agua.
   */
  onBlockChanged(x, y, z, oldBlock, newBlock) {
    // Si se rompio un bloque (ahora es aire), revisar si hay agua adyacente que deba fluir
    if (newBlock === BlockId.AIR && oldBlock !== BlockId.AIR) {
      // Limpiar nivel de agua si habia
      const key = this._key(x, y, z);
      this.waterLevels.delete(key);
      // Programar vecinos que son agua para que fluyan hacia el hueco
      this._scheduleNeighbors(x, y, z);
    }

    // Si se coloco agua, programar su procesamiento
    if (newBlock === BlockId.WATER) {
      // El agua colocada por el jugador es fuente (nivel 7)
      this._setWaterLevel(x, y, z, WATER_SOURCE_LEVEL);
      this.scheduleUpdate(x, y, z);
    }

    // Si se coloco un bloque solido donde habia agua, limpiar
    if (oldBlock === BlockId.WATER && newBlock !== BlockId.WATER && newBlock !== BlockId.AIR) {
      const key = this._key(x, y, z);
      this.waterLevels.delete(key);
    }
  }

  /**
   * Programar actualizacion de todos los vecinos que son agua.
   */
  _scheduleNeighbors(x, y, z) {
    const neighbors = [
      [1, 0, 0], [-1, 0, 0],
      [0, 1, 0], [0, -1, 0],
      [0, 0, 1], [0, 0, -1]
    ];
    for (const [dx, dy, dz] of neighbors) {
      const nx = x + dx;
      const ny = y + dy;
      const nz = z + dz;
      if (this.world.getBlock(nx, ny, nz) === BlockId.WATER) {
        this.scheduleUpdate(nx, ny, nz);
      }
    }
  }

  /**
   * Verificar si una posicion tiene agua fluyendo (para empujar al jugador).
   * Retorna la direccion del flujo como {x, z} o null si no hay flujo.
   */
  getFlowDirection(x, y, z) {
    if (this.world.getBlock(x, y, z) !== BlockId.WATER) return null;

    const myLevel = this.getWaterLevel(x, y, z);
    let flowX = 0;
    let flowZ = 0;

    // La corriente va de nivel alto a nivel bajo
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [dx, dz] of dirs) {
      const nx = x + dx;
      const nz = z + dz;
      const neighborBlock = this.world.getBlock(nx, y, nz);

      if (neighborBlock === BlockId.AIR) {
        // El agua quiere fluir hacia el aire
        flowX += dx;
        flowZ += dz;
      } else if (neighborBlock === BlockId.WATER) {
        const neighborLevel = this.getWaterLevel(nx, y, nz);
        if (neighborLevel < myLevel) {
          // Fluir hacia nivel mas bajo
          flowX += dx;
          flowZ += dz;
        } else if (neighborLevel > myLevel) {
          // Empujado desde nivel mas alto
          flowX -= dx;
          flowZ -= dz;
        }
      }
    }

    // Normalizar
    const len = Math.sqrt(flowX * flowX + flowZ * flowZ);
    if (len < 0.01) return null;

    return { x: flowX / len, z: flowZ / len };
  }
}
